import { inject, Injectable } from '@angular/core';
import { CrmFormConfigOptions, CrmFormProvider, crmFormRequiredValidator } from '@touch4it/ng-swp/form';
import { map, Observable, of, switchMap } from 'rxjs';
import { CrmFormFragment } from '@touch4it/ng-swp/form/fragments';
import { crmGetFormPassword, crmGetFormRadioButton } from '@touch4it/ng-swp/form/inputs';

import { UserModel } from '~/api/users/user.model';
import {
  canEditUserRole,
  getSelectableUserRole,
  selectableUserRoleOptions,
  SelectableUserRole,
} from '~/api/users/user.role';
import { getDetailFieldsFactory } from '~/features/users/factories/get-detail-fields.factory';
import { UsersApiService } from '~/api/users/users-api.service';

export type UserDetailFormData = {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: SelectableUserRole;
  password?: string;
};

@Injectable()
export class UserDetailFormProvider extends CrmFormProvider<UserModel, UserDetailFormData> {
  private getFields = getDetailFieldsFactory();
  private userService = inject(UsersApiService);

  protected override getFormFragments(options: CrmFormConfigOptions<UserModel>): Observable<CrmFormFragment[]> {
    return this.getFields().pipe(
      switchMap((fields) =>
        this.userService.user$.pipe(
          map((currentUser) => {
            const fragments = [...fields];

            fragments.push(
              crmGetFormPassword({
                name: 'password',
                config: {
                  label: 'generic.password',
                },
              }),
            );

            if (canEditUserRole(currentUser, options.data)) {
              fragments.push(
                crmGetFormRadioButton({
                  name: 'role',
                  config: {
                    label: 'generic.role',
                    validators: [crmFormRequiredValidator()],
                  },
                  options: {
                    options: selectableUserRoleOptions,
                  },
                }),
              );
            }

            return fragments;
          }),
        ),
      ),
    );
  }

  protected override getFormData({ data }: CrmFormConfigOptions<UserModel>): Observable<UserDetailFormData> {
    return of({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      role: getSelectableUserRole(data),
    });
  }
}
